import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import { buildDashboardDateParams } from '../../utils/dashboardChartData';

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const POSTED_DATE_OPTIONS = [
  { value: -1, label: 'All' },
  { value: 0, label: 'Any time' },
  { value: 1, label: 'Past 24 hours' },
  { value: 7, label: 'Past week' },
  { value: 30, label: 'Past month' },
  { value: 365, label: 'Past year' },
];

const formatPostedDateSelection = (selected) => {
  if (!selected || selected.length === 0) return 'All';
  return selected
    .map((value) => POSTED_DATE_OPTIONS.find((option) => option.value === value)?.label || `${value}`)
    .join(', ');
};

const JobLinkAnalyticsTab = ({ dateRange, fromDate = '', rangeLabel }) => {
  const theme = useTheme();
  const [skills, setSkills] = useState([]);
  const [total, setTotal] = useState(0);
  const [postedDays, setPostedDays] = useState([-1]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSkillStats = async () => {
      if ((dateRange === 'custom' || dateRange === 'single') && !fromDate) {
        setLoading(false);
        setSkills([]);
        setTotal(0);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/bid-links/tech-stack-stats`,
          {
            params: {
              ...buildDashboardDateParams({ days: dateRange, fromDate }),
              showBlacklisted: 'false',
              showHidden: 'false',
              postedDays: postedDays.join(','),
            },
          }
        );
        setSkills(response.data?.skills || []);
        setTotal(response.data?.total || 0);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load skill stats');
        setSkills([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchSkillStats();
  }, [dateRange, fromDate, postedDays]);

  const handlePostedDaysChange = (event) => {
    const values = event.target.value;
    let nextValues = Array.isArray(values) ? [...values] : [values];

    const wasAllSelected = postedDays.includes(-1);
    const isAllSelected = nextValues.includes(-1);

    if (isAllSelected && !wasAllSelected) {
      nextValues = [-1];
    } else if (nextValues.some((value) => [0, 1, 7, 30, 365].includes(value))) {
      nextValues = nextValues.filter((value) => value !== -1);
    } else if (nextValues.includes(-1)) {
      nextValues = [-1];
    }

    if (nextValues.length === 0) {
      nextValues = [-1];
    }

    setPostedDays(nextValues);
  };

  const tableHeadCellSx = useMemo(
    () => ({
      fontWeight: 600,
      color: 'text.primary',
      borderColor: 'divider',
    }),
    []
  );

  const tableCellSx = useMemo(
    () => ({
      color: 'text.primary',
      borderColor: 'divider',
    }),
    []
  );

  const chartSkills = useMemo(
    () => skills.filter((row) => row.skill !== 'Others'),
    [skills]
  );

  const chartHeight = Math.max(280, chartSkills.length * 28);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper sx={{ p: 2, color: 'text.primary' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'text.primary' }}>
              Tech Skill Frequency
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Skills extracted from each job link (JD technologies). Top 100 by occurrence; remaining
              skills are grouped as Others.
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="job-link-posted-date-label">Posted Date Range</InputLabel>
            <Select
              labelId="job-link-posted-date-label"
              label="Posted Date Range"
              multiple
              value={postedDays}
              onChange={handlePostedDaysChange}
              renderValue={formatPostedDateSelection}
            >
              {POSTED_DATE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Filter by when the job was posted, same as Bid Links. Dashboard date range still limits when
          links were collected.
          {!loading && ` ${total} skill mentions in selected period.`}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !skills.length ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No tech skills found on job links for {rangeLabel?.toLowerCase() || 'the selected period'}.
          </Typography>
        ) : (
          <Box sx={{ width: '100%', height: chartHeight }}>
            <ResponsiveContainer>
              <BarChart
                data={chartSkills}
                layout="vertical"
                margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{ fill: theme.palette.text.primary }}
                />
                <YAxis
                  type="category"
                  dataKey="skill"
                  width={150}
                  tick={{ fill: theme.palette.text.primary, fontSize: 11 }}
                />
                <Tooltip
                  formatter={(value, _name, props) => [
                    `${value} mentions (${formatPercent(props.payload.percent)})`,
                    'Count',
                  ]}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                  }}
                />
                <Bar dataKey="count" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>

      {!loading && !error && skills.length > 0 && (
        <Paper sx={{ p: 2, color: 'text.primary' }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
            Skill Breakdown
          </Typography>
          <TableContainer>
            <Table
              size="small"
              sx={{
                '& .MuiTableCell-root': {
                  color: 'inherit',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>#</TableCell>
                  <TableCell sx={tableHeadCellSx}>Skill</TableCell>
                  <TableCell align="right" sx={tableHeadCellSx}>
                    Mentions
                  </TableCell>
                  <TableCell align="right" sx={tableHeadCellSx}>
                    Share
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skills.map((row, index) => (
                  <TableRow key={row.skill} hover>
                    <TableCell sx={tableCellSx}>
                      {row.skill === 'Others' ? '—' : index + 1}
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      {row.skill === 'Others' && row.groupedSkillCount
                        ? `Others (${row.groupedSkillCount} skills)`
                        : row.skill}
                    </TableCell>
                    <TableCell align="right" sx={tableCellSx}>
                      {row.count}
                    </TableCell>
                    <TableCell align="right" sx={tableCellSx}>
                      {formatPercent(row.percent)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default JobLinkAnalyticsTab;
